import { useMemo } from 'react';

import type { CommentType } from '../../../types/comments';
import { isOpenEdition } from '../MintComponents/shared-helpers';
import {
  DESKTOP_FIRST_MAX_IMAGE_SIZE,
  DESKTOP_LAST_MAX_IMAGE_SIZE,
  DESKTOP_MID_MAX_IMAGE_SIZE,
  TABLET_FIRST_MAX_IMAGE_SIZE,
  TABLET_LAST_MAX_IMAGE_SIZE,
  TABLET_MID_MAX_IMAGE_SIZE,
  TOP_ROW,
} from './shared/constants';

// Number of backers before switching to the windowed components
export const AUDIENCE_WINDOW_BACKERS_THRESHOLD = 100;

const EMPTY_SEATS_THRESHOLD = 25;

/** Helper function that powers empty seating logic during open editions */
export const seatsForOpenEdition = ({
  quantityLowerBound,
  backersData,
  maxSeats,
}: {
  quantityLowerBound: number;
  backersData: CommentType[] | undefined;
  maxSeats: number;
}) => {
  // If we have backers, sale is running
  if (backersData) {
    const lowerBound = quantityLowerBound ?? 0;

    // We have enough backers to fill the lower bound
    // just return the backers
    if (backersData.length >= lowerBound) {
      return backersData;
    }

    // We do not have enough backers to fill the lower bound
    return Array<CommentType | undefined>(lowerBound)
      .fill(undefined)
      .map((_v, index) => backersData[index]);
  }

  // The sale is not running and we have no backers.
  return Array<CommentType | undefined>(maxSeats).fill(undefined);
};

export function useAudienceBackersWithEmpties({
  numberOfSeats,
  quantityUpperBound,
  backersData,
  isPrimarySaleActive,
  quantityLowerBound,
  expandEmpties = true,
  maxSeats = 100,
}: {
  numberOfSeats: number;
  quantityUpperBound: number | undefined;
  quantityLowerBound: number | undefined;
  backersData: CommentType[] | undefined;
  isPrimarySaleActive: boolean;
  expandEmpties?: boolean;
  /* when we want to limit the number of empty seats returned */
  maxSeats?: number;
}) {
  return useMemo(() => {
    const isOpen = isOpenEdition(quantityUpperBound ?? 0);

    if (backersData?.length === 0 && !expandEmpties)
      return Array<CommentType | undefined>(EMPTY_SEATS_THRESHOLD).fill(undefined);

    if (isOpen) {
      return seatsForOpenEdition({
        quantityLowerBound: quantityLowerBound ?? 0,
        backersData,
        maxSeats,
      });
    }

    const initialMaxEmptySeats =
      isPrimarySaleActive && quantityUpperBound != null
        ? Math.max(numberOfSeats, quantityUpperBound)
        : Math.min(maxSeats, numberOfSeats);

    /**
     * temp quick fix for the case where we have a lot of empty seats
     *
     * empty seats only really matter when we have a small number of seats (less than 51) so for now we if maxEmptySeats is greater than 100
     * we just return the maxSeats
     */
    const maxEmptySeats =
      initialMaxEmptySeats > 100 ? Math.min(maxSeats, numberOfSeats) : initialMaxEmptySeats;

    // A performance optimization to avoid rendering too many empty seats
    // 100 is a lot of dom nodes to render but better than 10k
    const MAX_EMPTY_SEATS_SAFE = maxEmptySeats > 100 ? 100 : maxEmptySeats;

    if (backersData) {
      if (backersData.length >= maxEmptySeats) {
        return backersData;
      }

      return Array<CommentType | undefined>(MAX_EMPTY_SEATS_SAFE)
        .fill(undefined)
        .map((_v, index) => backersData[index]);
    }

    return Array<CommentType | undefined>(MAX_EMPTY_SEATS_SAFE).fill(undefined);
  }, [
    quantityUpperBound,
    backersData,
    expandEmpties,
    isPrimarySaleActive,
    numberOfSeats,
    maxSeats,
    quantityLowerBound,
  ]);
}

export function getRow({ index, midRowMax }: { index: number; midRowMax: number }) {
  const firstRow = index < TOP_ROW;
  const midRow = index >= TOP_ROW && index < midRowMax * 2 + TOP_ROW;
  return firstRow ? 'first' : midRow ? 'mid' : 'last';
}

export function getRowV2({
  index,
  midRowMax,
  mobileBreakpoint,
}: {
  index: number;
  midRowMax: number;
  mobileBreakpoint: boolean;
}) {
  const firstRow = index < TOP_ROW;
  const midRow = index >= TOP_ROW && index < midRowMax * 2 + TOP_ROW;
  return firstRow ? 'first' : midRow ? 'mid' : mobileBreakpoint ? 'mid' : 'last';
}

export const getImageSize = ({
  desktopBreakpoint,
  rowPos,
}: {
  desktopBreakpoint?: boolean;
  rowPos: 'first' | 'mid' | 'last';
}) => {
  if (desktopBreakpoint) {
    switch (rowPos) {
      case 'first':
        return DESKTOP_FIRST_MAX_IMAGE_SIZE;

      case 'mid':
        return DESKTOP_MID_MAX_IMAGE_SIZE;

      case 'last':
        return DESKTOP_LAST_MAX_IMAGE_SIZE;
    }
  }

  switch (rowPos) {
    case 'first':
      return TABLET_FIRST_MAX_IMAGE_SIZE;

    case 'mid':
      return TABLET_MID_MAX_IMAGE_SIZE;

    case 'last':
      return TABLET_LAST_MAX_IMAGE_SIZE;
  }
};
